import { coerce, object, string, date, boolean, ZodIssueCode, unknown } from 'zod';

import { makeZodValidate } from '@occc/ui-forms';
import {
  IS_REQUIRED,
  IS_VALID,
  IS_LATITUDE_VALID_RANGE,
  IS_LONGITUDE_VALID_RANGE,
} from '@occc-app/lib/constants/validationMessages';
import { WEEKDAY } from '@occc-app/lib/util-location-schedule/location-schedule.types';
import { validatePhoneNumber } from '@occc/utils-phone-number';

const hexColorValidation = string()
  .regex(/^#[0-9A-Fa-f]{6}$/, IS_VALID)
  .optional()
  .nullable();

const BOTH_COLORS_REQUIRED_MESSAGE =
  'Primary Color and Secondary Color must both be set, or both left empty.';

export const brandingDetailsValidation = object({
  primaryColor: hexColorValidation,
  secondaryColor: hexColorValidation,
})
  .superRefine((data, ctx) => {
    const hasPrimary = !!data.primaryColor;
    const hasSecondary = !!data.secondaryColor;

    if (hasPrimary === hasSecondary) {
      return;
    }

    if (!hasPrimary) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['primaryColor'],
        message: BOTH_COLORS_REQUIRED_MESSAGE,
      });
    }

    if (!hasSecondary) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['secondaryColor'],
        message: BOTH_COLORS_REQUIRED_MESSAGE,
      });
    }
  })
  .optional()
  .nullable();

const detailsValidation = object({
  name: string({ required_error: IS_REQUIRED }),
  street1: string({ required_error: IS_REQUIRED }),
  street2: string().optional(),
  city: string({ required_error: IS_REQUIRED }),
  state: object(
    {
      id: string(),
      text: string(),
    },
    { required_error: IS_REQUIRED, invalid_type_error: IS_REQUIRED },
  ),
  zipCode: string({ required_error: IS_REQUIRED }).regex(/^[0-9]{5}$/, 'Zip code must be 5 digits'),
  phoneNumber: string().optional().refine(validatePhoneNumber, {
    message: IS_VALID,
  }),
  brandingDetails: brandingDetailsValidation,
});

const geolocationValidation = object({
  latitude: coerce
    .number()
    .min(-90, IS_LATITUDE_VALID_RANGE)
    .max(90, IS_LATITUDE_VALID_RANGE)
    .optional(),
  longitude: coerce
    .number()
    .min(-180, IS_LONGITUDE_VALID_RANGE)
    .max(180, IS_LONGITUDE_VALID_RANGE)
    .optional(),
})
  .optional()
  .nullable()
  .refine(
    (data) => {
      return !(
        (data?.latitude === undefined && data?.longitude !== undefined) ||
        (data?.longitude === undefined && data?.latitude !== undefined)
      );
    },
    (data) => {
      if (data?.latitude === undefined && data?.longitude !== undefined) {
        return {
          message: 'Longitude cannot be submitted without latitude.',
          path: ['latitude'],
        };
      }

      return {
        message: 'Latitude cannot be submitted without longitude.',
        path: ['longitude'],
      };
    },
  );

const dayScheduleValidator = object({
  isOpenAllDay: boolean().optional(),
  // accept whatever the input is; we'll decide later
  start: unknown().optional(),
  end: unknown().optional(),
})
  .optional()
  .superRefine((data, ctx) => {
    if (!data || data.isOpenAllDay) {
      return;
    }

    const parsedStart =
      data.start === null || data.start === undefined ? null : date().safeParse(data.start);
    const parsedEnd =
      data.end === null || data.end === undefined ? null : date().safeParse(data.end);

    if (!parsedStart || !parsedStart.success) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['start'],
        message: 'Value is not a valid date',
      });
    }

    if (!parsedEnd || !parsedEnd.success) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['end'],
        message: 'Value is not a valid date',
      });
    }

    if (parsedStart?.success && parsedEnd?.success && parsedEnd.data <= parsedStart.data) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['end'],
        message: 'End date cannot be earlier than start date.',
      });
    }
  });

export const scheduleValidation = object({
  [WEEKDAY.FRIDAY]: dayScheduleValidator,
  [WEEKDAY.MONDAY]: dayScheduleValidator,
  [WEEKDAY.SATURDAY]: dayScheduleValidator,
  [WEEKDAY.SUNDAY]: dayScheduleValidator,
  [WEEKDAY.THURSDAY]: dayScheduleValidator,
  [WEEKDAY.TUESDAY]: dayScheduleValidator,
  [WEEKDAY.WEDNESDAY]: dayScheduleValidator,
});

const retailerNetworkLocationUpdateSchema = object({
  details: detailsValidation,
  geoLocation: geolocationValidation,
  schedule: scheduleValidation,
});

export const validateRetailerNetworkLocationUpdateForm = makeZodValidate(
  retailerNetworkLocationUpdateSchema,
);
